import { LegacyError, throwIfGraphError } from './errors';
import {
  Command,
  CommandResult,
  PushHandler,
  Query,
  QueryResult,
  Transport,
  Unsubscribe,
} from './types';

/**
 * HTTP transport for the generic Graph CQRS endpoints:
 * `POST /graph/query` and `PUT /graph/command`. This is the original
 * `OneService.send` implementation, extracted behind the `Transport` interface.
 * There is no push channel over HTTP.
 */
export class HttpTransport implements Transport {
  readonly kind = 'http' as const;

  constructor(
    private readonly baseUrl: string,
    private readonly getAccessToken: () => Promise<string>
  ) {}

  // HTTP is stateless — nothing to connect or tear down.
  async connect(): Promise<void> {}
  dispose(): void {}

  // No server push over plain HTTP; accept the handler for `Transport` parity
  // and hand back a no-op unsubscribe.
  subscribePush(_handler: PushHandler): Unsubscribe {
    return () => {};
  }

  query<R extends QueryResult = QueryResult>(
    query: Query,
    signal?: AbortSignal
  ): Promise<R> {
    return this.send<R>('query', query, signal);
  }

  command<R extends CommandResult = CommandResult>(
    command: Command,
    signal?: AbortSignal
  ): Promise<R> {
    return this.send<R>('command', command, signal);
  }

  private async send<R>(
    kind: 'query' | 'command',
    payload: Query | Command,
    signal?: AbortSignal
  ): Promise<R> {
    const token = await this.getAccessToken();

    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}/graph/${kind}`, {
        method: kind === 'query' ? 'POST' : 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [kind]: payload }),
        // Match the axios client (`withCredentials: true`) and the SignalR
        // connection: send cookies so cookie-based auth / session affinity works.
        credentials: 'include',
        signal,
      });
    } catch (e) {
      // Preserve caller-driven cancellation; wrap genuine network failures
      // (no HTTP status → E_NO_CONNECTION).
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new LegacyError({ message: (e as Error)?.message, cause: e });
    }

    let data: any;
    try {
      data = await res.json();
    } catch {
      data = null;
    }

    // A legacy/transport failure (no GraphError body) → map status to a code.
    if (!res.ok) {
      throw new LegacyError({ status: res.status, body: data });
    }

    // A 2xx may still carry a Graph-level `result.error`; surface it uniformly.
    return throwIfGraphError(data.result as R);
  }
}
